import { useContext, useState } from "react";
import ToastMsg from "../../components/common/ToastMsg";
import { SETTINGS_CHANGE_PASSWORD } from "../../constants/api";
import useApiMethods from "../../lib/useApiMethods";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Eye, EyeClosed, CheckCircle, XCircle, AlertTriangle, LogOut, Key, Shield, Clock } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";

const PasswordInput = ({ value, onChange, placeholder, label }) => {
    const [showPassword, setShowPassword] = useState(false);

    return (
        <div className="relative">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</label>
            <Input
                type={showPassword ? "text" : "password"}
                value={value}
                onChange={onChange}
                className="w-full pr-10 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none transition-all duration-200"
                placeholder={placeholder}
            />
            <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-9 text-gray-400 hover:text-blue-600 transition-colors duration-200"
            >
                {showPassword ? <Eye size={18} /> : <EyeClosed size={18} />}
            </button>
        </div>
    );
};

const PasswordRequirement = ({ met, text }) => (
    <div className="flex items-center gap-3">
        {met ? (
            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
        ) : (
            <XCircle className="h-4 w-4 text-red-500 flex-shrink-0" />
        )}
        <span className={`text-sm ${met ? 'text-green-700 dark:text-green-400' : 'text-red-700 dark:text-red-400'}`}>
            {text}
        </span>
    </div>
);

const Security = () => {
    let navigate = useNavigate();
    const { logoutauth } = useContext(AuthContext);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

    const { create } = useApiMethods();

    const passwordPolicy = sessionStorage.getItem('password_policy');
    const parsedPolicy = passwordPolicy ? JSON.parse(passwordPolicy) : null;

    // Password validation functions
    const validatePassword = (password) => {
        if (!parsedPolicy) return { isValid: true, requirements: [] };

        const requirements = [
            {
                met: password.length >= parsedPolicy.min_length,
                text: `Minimum ${parsedPolicy.min_length} characters`
            },
            {
                met: password.length <= parsedPolicy.max_length,
                text: `Maximum ${parsedPolicy.max_length} characters`
            },
            {
                met: (password.match(/\d/g) || []).length >= parsedPolicy.min_number,
                text: `At least ${parsedPolicy.min_number} number(s)`
            },
            {
                met: (password.match(/[a-z]/g) || []).length >= parsedPolicy.min_lower,
                text: `At least ${parsedPolicy.min_lower} lowercase letter(s)`
            },
            {
                met: (password.match(/[A-Z]/g) || []).length >= parsedPolicy.min_upper,
                text: `At least ${parsedPolicy.min_upper} uppercase letter(s)`
            },
            {
                met: (password.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || []).length >= parsedPolicy.min_chars,
                text: `At least ${parsedPolicy.min_chars} special character(s)`
            }
        ];

        const isValid = requirements.every(req => req.met);

        return { isValid, requirements };
    };

    const { isValid: isNewPasswordValid, requirements: newPasswordRequirements } = validatePassword(newPassword);

    const handleChangePassword = async () => {
        // Basic validation
        if (!currentPassword || !newPassword || !confirmPassword) {
            ToastMsg('error', 'Please fill in all fields');
            return;
        }

        if (newPassword !== confirmPassword) {
            ToastMsg('error', 'New password and confirm password do not match');
            return;
        }

        // Password policy validation
        if (parsedPolicy && !isNewPasswordValid) {
            ToastMsg('error', 'New password does not meet the requirements');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                current_password: currentPassword,
                new_password: newPassword
            };
            const response = await create(SETTINGS_CHANGE_PASSWORD, payload);

            if (response.status === 'success') {
                ToastMsg('success', response.message);
                setCurrentPassword('');
                setNewPassword('');
                setConfirmPassword('');
                setShowLogoutConfirm(true)
            }
        } catch (error) {
            ToastMsg('error', error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleLogoutAllDevices = () => {
        ToastMsg('success', 'Logged out from all devices');
        setShowLogoutConfirm(false);
        logoutauth()
        navigate('/')
    };

    // Add this helper function outside your component
    const calculateSecurityScore = (policy) => {
        if (!policy) return 85;

        let score = 50; // Base score

        // Add points for each security feature
        if (policy.session_time_out) score += 15;
        if (policy.login_attempts) score += 10;
        if (policy.password_repetation) score += 10;
        if (policy.expairy_period && policy.expairy_period <= 90) score += 15;

        return Math.min(100, score);
    };

    return (
        <div className="p-0">
            <div className="mb-6">
                <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                    Security & Access
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                    Manage your password and account security settings
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Password Change Form */}
                <div className="bg-gradient-to-br from-white to-white dark:from-gray-800 dark:to-gray-900 rounded-xl border border-gray-300 dark:border-gray-700 p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                            <Key className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                        </div>
                        <div>
                            <h2 className="text-md font-semibold text-gray-900 dark:text-white">
                                Change Password
                            </h2>
                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                Update your password to keep your account secure
                            </p>
                        </div>
                    </div>

                    <div className="space-y-5">
                        <PasswordInput
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            placeholder="Enter your current password"
                            label="Current Password"
                        />

                        <PasswordInput
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Create a new password"
                            label="New Password"
                        />

                        {/* Real-time Password Requirements */}
                        {parsedPolicy && newPassword && (
                            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 shadow-sm">
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                                    <Shield className="h-4 w-4 text-blue-500" />
                                    Password Requirements
                                </h3>
                                <div className="space-y-2.5">
                                    {newPasswordRequirements.map((requirement, index) => (
                                        <PasswordRequirement
                                            key={index}
                                            met={requirement.met}
                                            text={requirement.text}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        <PasswordInput
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            placeholder="Confirm your new password"
                            label="Confirm New Password"
                        />

                        {/* Password Match Validation */}
                        {newPassword && confirmPassword && (
                            <div className={`flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 ${newPassword === confirmPassword
                                ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800 text-green-800 dark:text-green-300'
                                : 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800 text-red-800 dark:text-red-300'
                                }`}>
                                {newPassword === confirmPassword ? (
                                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                                ) : (
                                    <XCircle className="h-5 w-5 text-red-500 flex-shrink-0" />
                                )}
                                <span className="text-sm font-medium">
                                    {newPassword === confirmPassword
                                        ? 'Passwords match perfectly'
                                        : 'Passwords do not match'
                                    }
                                </span>
                            </div>
                        )}

                        {/* Submit Button */}
                        <Button
                            onClick={handleChangePassword}
                            disabled={loading || !isNewPasswordValid || newPassword !== confirmPassword}
                            className="w-full bg-gradient-to-r from-sidebar to-sidebar-primary hover:from-sidebar-primary hover:to-sidebar disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-[1.02] disabled:scale-100 shadow-md"
                        >
                            {loading ? (
                                <div className="flex items-center gap-2">
                                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Changing Password...
                                </div>
                            ) : (
                                <div className="flex items-center gap-2">
                                    <Key className="h-4 w-4" />
                                    Update Password
                                </div>
                            )}
                        </Button>
                    </div>
                </div>

                {/* Right Column - Security Information & Danger Zone */}
                <div className="space-y-6">
                    {/* Security Status */}
                    <div className="bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 rounded-2xl border border-gray-100 dark:border-gray-700 p-6 shadow-lg">
                        {/* Header */}
                        <div className="flex items-start gap-3 mb-6">
                            <div className="p-3 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl shadow-sm">
                                <Shield className="h-6 w-6 text-green-600 dark:text-green-400" />
                            </div>
                            <div className="flex-1">
                                <h2 className="text-md font-bold text-gray-900 dark:text-white">
                                    Security Status
                                </h2>
                                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    Overview of your account security and protection status
                                </p>
                            </div>
                            <div className="px-3 py-1 bg-green-100 dark:bg-green-900/30 rounded-full">
                                <span className="text-xs font-semibold text-green-700 dark:text-green-400 uppercase tracking-wide">
                                    Protected
                                </span>
                            </div>
                        </div>

                        {/* Security Metrics Grid */}
                        <div className="grid grid-cols-1 gap-4">
                            {/* Password Expiry Card */}
                            <div className="bg-white dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-600 p-4 shadow-sm hover:shadow-md transition-all duration-200">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                                            <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
                                        </div>
                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                                Password Expiry
                                            </h3>
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                Days until password reset required
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                                            {parsedPolicy?.expairy_period || 0}
                                        </div>
                                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                            days left
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Session Security Card */}
                            <div className="bg-white dark:bg-gray-800/80 rounded-xl border border-gray-200 dark:border-gray-600 p-4 shadow-sm hover:shadow-md transition-all duration-200">
                                <div className="flex items-center gap-3">
                                    <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                                        <Shield className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                                            Session Security
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {parsedPolicy?.session_time_out ? (
                                                <span className="flex items-center gap-2">
                                                    <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                                                    Auto-logout after <span className="font-semibold text-blue-600 dark:text-blue-400">{parsedPolicy.time_out_value || 30} minutes</span> of inactivity
                                                </span>
                                            ) : (
                                                <span className="flex items-center gap-2">
                                                    <span className="w-2 h-2 bg-gray-400 rounded-full"></span>
                                                    Session timeout disabled
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Security Features Quick View */}
                            {/* <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 rounded-xl border border-blue-200 dark:border-blue-800 p-4">
                                <h4 className="text-xs font-semibold text-blue-800 dark:text-blue-300 uppercase tracking-wide mb-3">
                                    Active Security Features
                                </h4>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        <span className="text-xs text-gray-700 dark:text-gray-300">Password Policy</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                        <span className="text-xs text-gray-700 dark:text-gray-300">Session Control</span>
                                    </div>
                                    {parsedPolicy?.login_attempts && (
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            <span className="text-xs text-gray-700 dark:text-gray-300">Login Attempts</span>
                                        </div>
                                    )}
                                    {parsedPolicy?.password_repetation && (
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                            <span className="text-xs text-gray-700 dark:text-gray-300">No Repeats</span>
                                        </div>
                                    )}
                                </div>
                            </div> */}
                        </div>

                        {/* Security Score */}
                        {/* <div className="mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                                        Security Score
                                    </h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">
                                        Based on active security features
                                    </p>
                                </div>
                                <div className="text-right">
                                    <div className="text-lg font-bold text-green-600 dark:text-green-400">
                                        {calculateSecurityScore(parsedPolicy)}%
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        Excellent
                                    </div>
                                </div>
                            </div>
                        </div> */}
                    </div>

                    {/* Danger Zone */}
                    <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800 rounded-xl p-6 shadow-sm">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
                            </div>
                            <div>
                                <h2 className="text-md font-semibold text-red-800 dark:text-red-300">
                                    Danger Zone
                                </h2>
                                <p className="text-xs text-red-700 dark:text-red-400 mt-1">
                                    Critical actions that affect your account
                                </p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-lg border border-red-200 dark:border-red-700">
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                        Logout All Devices
                                    </h3>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                        Sign out from all active sessions
                                    </p>
                                </div>
                                <Button
                                    onClick={() => setShowLogoutConfirm(true)}
                                    className="bg-red-600 hover:bg-red-700 text-white font-medium py-2 px-4 rounded-lg transition-colors duration-200 shadow-sm"
                                >
                                    <LogOut className="h-4 w-4 mr-2" />
                                    Logout All
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Logout Confirmation Modal */}
            {showLogoutConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-md">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 max-w-md w-full shadow-xl border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="p-2 bg-red-100 dark:bg-red-900/30 rounded-lg">
                                <AlertTriangle className="h-6 w-6 text-red-600" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Logout All Devices?
                            </h3>
                        </div>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 text-sm leading-relaxed">
                            This action will immediately sign you out from all active sessions across all devices. You'll need to log in again on each device.
                        </p>
                        <div className="flex gap-3 justify-end">
                            <Button
                                variant="outline"
                                onClick={() => setShowLogoutConfirm(false)}
                                className="border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 px-6"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleLogoutAllDevices}
                                className="bg-red-600 hover:bg-red-700 text-white px-6 shadow-sm"
                            >
                                <LogOut className="h-4 w-4 mr-2" />
                                Confirm Logout
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Security;